<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';
import ButtonSecondary from '@/shared/components/button/ButtonSecondary.vue';
import SendEmailIcon from '../assets/icon/send-email.svg';
import RemoveIcon from '../assets/icon/trash.svg';
import CheckCircleIcon from '@shared/assets/icon/check-circle-filled.svg';
import type { FormInstance } from 'ant-design-vue';
import type { Rule } from 'ant-design-vue/es/form';
import type { AssetManagerInvite } from '../models/Project';
import { EmailStatus } from '../models/Project';
import { validateIsEmail } from '@/utils/validators';

const props = defineProps<{
  id: number;
  email: string;
  status: EmailStatus | '';
  existingInvites: AssetManagerInvite[];
}>();

const emit = defineEmits<{
  (e: 'invite'): void;
  (e: 'remove'): void;
  (e: 'change', value: string): void;
}>();

const formRef = ref<FormInstance>();
const formState = ref({
  email: props.email,
  id: props.id
});
const status = computed(() => props.status);
const disabled = computed(() =>
  [EmailStatus.Sending, EmailStatus.Sent].includes(status.value as EmailStatus),
);
const rules: Record<string, Rule[]> = {
  email: [
    {
      validator: (_, value) => {
        if (!value) {
          return Promise.reject('Email is required');
        }

        if (!validateIsEmail(value)) {
          return Promise.reject('Please enter a valid email address');
        }

        if (
          props.existingInvites?.some(
            (invite, index) => invite.email === value && index !== props.id,
          )
        ) {
          return Promise.reject('Email already exists');
        }

        return Promise.resolve();
      },
      trigger: ['blur', 'change'],
    },
  ],
};

const invite = async () => {
  try {
    await formRef.value?.validate();
    emit('invite');
  } catch {
    // validation errors shown by form
  }
};
const remove = () => {
  emit('remove');
};
const change = (e: Event) => {
  const value = (e.target as HTMLInputElement).value;
  emit('change', value);
};

const getTooltipContainer = (trigger: HTMLElement) => trigger.parentElement ?? document.body;

const refreshValidation = () => {
  formRef.value?.clearValidate(['email']);
  formRef.value?.validate(['email']);
};

watch(
  () => props.email,
  (nextEmail) => {
    formState.value.email = nextEmail;
    refreshValidation();
  },
);

watch(
  () => props.id,
  (nextId) => {
    formState.value.id = nextId;
    refreshValidation();
  },
);

watch(
  () => props.existingInvites,
  () => {
    refreshValidation();
  },
  { deep: true },
);
</script>

<template>
  <div class="invite-card">
    <a-form ref="formRef" class="input-group" :model="formState" :rules="rules" layout="vertical">
      <a-form-item name="email" :validate-first="true">
        <a-input
          v-model:value="formState.email"
          size="large"
          placeholder="Email address"
          :disabled="disabled"
          @change="change"
        />
      </a-form-item>
    </a-form>

    <template v-if="status === 'sent'">
      <a-tooltip
        title="Invite sent"
        :getPopupContainer="getTooltipContainer"
        :destroyTooltipOnHide="true"
        data-tn="invite-sent-tooltip"
      >
        <CheckCircleIcon class="action-icon action-icon__success" />
      </a-tooltip>
    </template>
    <template v-else>

      <ButtonPrimary
        type="primary"
        @click="invite"
        :disabled="disabled"
        class="action-button"
        aria-label="Send invite"
        data-tn="send-invite-btn"
      >
        <template #iconLeft>
          <a-tooltip
            title="Send invite"
            :getPopupContainer="getTooltipContainer"
            :destroyTooltipOnHide="true"
          >
            <SendEmailIcon class="action-icon" />
          </a-tooltip>
        </template>
      </ButtonPrimary>
      
      <ButtonSecondary
        type="secondary"
        @click="remove"
        :disabled="disabled"
        class="action-button"
        data-tn="remove-email-btn"
        ><template #iconLeft>
          <a-tooltip
            title="Remove email"
            :getPopupContainer="getTooltipContainer"
            :destroyTooltipOnHide="true"
          >
            <RemoveIcon class="action-icon" />
          </a-tooltip>
        </template>
      </ButtonSecondary>
    </template>
  </div>
</template>

<style lang="less" scoped>
.invite-card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
}

.input-group {
  flex: 1 1 auto;
}

:deep(.input-group .ant-form-item-explain) {
  text-align: left;
  padding-left: 5px;
}

:deep(.ant-form .ant-form-item) {
  margin: 0;
}

.action-button {
  width: 50px;
  min-width: 50px;
  padding: 0;
  border-radius: 50%;
}

:deep(.action-button .icon-left) {
  left: 50%;
  transform: translate(-50%, -50%);
}

:deep(.action-icon) {
  width: 25px;
  height: 25px;
  &__success {
    color: @color-primary;
    margin-top: 8px;
  }
}
</style>